'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  BuildingOfficeIcon,
  BellIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';
import { useNotifications } from '@/hooks/useNotifications';

const DAYS: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DAY_SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed',
  Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat',
};

interface TimetableSlot {
  id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  room: string;
  semester: string;
  slot_status: string;
  course_code: string;
  course_name: string;
  trainer_name: string;
  campus_name: string;
}

interface ExamEvent {
  id: string;
  exam_date: string;
  start_time: string;
  end_time: string;
  room: string;
  semester: string;
  cohort: string;
  notes: string;
  courses: { code: string; name: string } | null;
  campuses: { name: string } | null;
  invigilator: { full_name: string } | null;
}

interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  label: string;
  courseCode: string;
  room: string;
  isNow: boolean;
}

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getNextClassInfo(slots: TimetableSlot[]): { slot: TimetableSlot | null; targetDate: Date | null } {
  if (slots.length === 0) return { slot: null, targetDate: null };

  const now = new Date();
  const dayIndex = now.getDay(); // 0=Sun, 1=Mon...
  const dayMap: Record<string, number> = {
    Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6,
  };

  let best: { slot: TimetableSlot; targetDate: Date } | null = null;

  for (const slot of slots) {
    if (slot.slot_status !== 'published') continue;
    const slotDay = dayMap[slot.day_of_week] ?? -1;
    if (slotDay === -1) continue;

    const [sh, sm] = slot.start_time.split(':').map(Number);
    const [eh, em] = slot.end_time.split(':').map(Number);

    // Build candidate date for this week
    let daysAhead = slotDay - dayIndex;
    if (daysAhead < 0) daysAhead += 7;

    const candidate = new Date(now);
    candidate.setDate(now.getDate() + daysAhead);
    candidate.setHours(sh, sm, 0, 0);

    const endCandidate = new Date(now);
    endCandidate.setDate(now.getDate() + daysAhead);
    endCandidate.setHours(eh, em, 0, 0);

    // If today and class already ended, push to next week
    if (daysAhead === 0 && endCandidate <= now) {
      candidate.setDate(candidate.getDate() + 7);
    }

    if (!best || candidate < best.targetDate) {
      best = { slot, targetDate: candidate };
    }
  }

  return best ? { slot: best.slot, targetDate: best.targetDate } : { slot: null, targetDate: null };
}

function useCountdown(targetDate: Date | null): CountdownState | null {
  const [state, setState] = useState<CountdownState | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!targetDate) { setState(null); return; }

    const tick = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff <= 0) {
        setState(null);
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setState((prev) => ({
        ...(prev ?? { label: '', courseCode: '', room: '', isNow: false }),
        days, hours, minutes, seconds,
        isNow: diff < 60000,
      }));
      rafRef.current = window.setTimeout(tick, 1000);
    };

    tick();
    return () => { if (rafRef.current) clearTimeout(rafRef.current); };
  }, [targetDate]);

  return state;
}

function generateIcal(slots: TimetableSlot[], semesterLabel: string): string {
  const dayMap: Record<string, string> = {
    Monday: 'MO', Tuesday: 'TU', Wednesday: 'WE',
    Thursday: 'TH', Friday: 'FR', Saturday: 'SA',
  };

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SILAG-SIMS//Student Timetable//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${semesterLabel} Timetable`,
    'X-WR-TIMEZONE:Pacific/Port_Moresby',
  ];

  // Use a fixed reference Monday (first Monday of the semester period)
  const refMonday = new Date('2026-07-27'); // Semester 1, 2026 reference week

  slots.forEach((slot) => {
    if (slot.slot_status !== 'published') return;
    const byday = dayMap[slot.day_of_week] ?? 'MO';
    const dayOffset = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(slot.day_of_week);
    const startDate = new Date(refMonday);
    startDate.setDate(refMonday.getDate() + dayOffset);

    const [sh, sm] = slot.start_time.split(':').map(Number);
    const [eh, em] = slot.end_time.split(':').map(Number);

    const pad = (n: number) => n.toString().padStart(2, '0');
    const dateStr = `${startDate.getFullYear()}${pad(startDate.getMonth() + 1)}${pad(startDate.getDate())}`;
    const dtStart = `${dateStr}T${pad(sh)}${pad(sm)}00`;
    const dtEnd = `${dateStr}T${pad(eh)}${pad(em)}00`;

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:silag-${slot.id}@silag-sims.org`);
    lines.push(`DTSTART;TZID=Pacific/Port_Moresby:${dtStart}`);
    lines.push(`DTEND;TZID=Pacific/Port_Moresby:${dtEnd}`);
    lines.push(`RRULE:FREQ=WEEKLY;BYDAY=${byday};COUNT=16`);
    lines.push(`SUMMARY:${slot.course_code} — ${slot.course_name}`);
    lines.push(`LOCATION:${slot.room}, ${slot.campus_name}`);
    lines.push(`DESCRIPTION:Trainer: ${slot.trainer_name}\\nCampus: ${slot.campus_name}`);
    lines.push('STATUS:CONFIRMED');
    lines.push('END:VEVENT');
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

interface Props {
  studentId: string;
  semester: string;
  campusId: string | null;
}

export default function StudentTimetableTab({ studentId, semester, campusId }: Props) {
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [examEvents, setExamEvents] = useState<ExamEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDay, setActiveDay] = useState<string>('All');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [timetableView, setTimetableView] = useState<'classes' | 'exams'>('classes');

  const { notifications, markAsRead } = useNotifications();

  // Filter schedule-change notifications
  const scheduleNotifications = notifications.filter(
    (n) => !n.isRead && (n.eventType === 'schedule_change' || n.eventType === 'schedule_update' || n.title?.toLowerCase().includes('schedule'))
  );

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();

      // Get student's enrolled course IDs
      const { data: enrollData, error: enrollErr } = await supabase
        .from('enrollments').select('courses(id)').eq('student_id', studentId).neq('enrollment_status', 'withdrawn');

      if (enrollErr) throw new Error(enrollErr.message);

      const courseIds: string[] = (enrollData || [])
        .map((e: any) => e.courses?.id)
        .filter(Boolean);

      if (courseIds.length === 0) {
        setSlots([]);
        setExamEvents([]);
        setLoading(false);
        return;
      }

      // Fetch schedule slots for enrolled courses
      const { data: slotData, error: slotErr } = await supabase
        .from('schedule_slots').select(`id,day_of_week,start_time,end_time,room,semester,slot_status,courses(code, name),campuses(name),user_profiles(full_name)`).in('course_id', courseIds).in('slot_status', ['published', 'draft'])
        .order('day_of_week').order('start_time');

      if (slotErr) throw new Error(slotErr.message);

      const mapped: TimetableSlot[] = (slotData || []).map((s: any) => ({
        id: s.id,
        day_of_week: s.day_of_week,
        start_time: s.start_time,
        end_time: s.end_time,
        room: s.room || '—',semester: s.semester || semester,slot_status: s.slot_status,course_code: s.courses?.code || '—',course_name: s.courses?.name || '—',trainer_name: s.user_profiles?.full_name || 'TBA',campus_name: s.campuses?.name || '—',
      }));

      setSlots(mapped);

      // Fetch published exam events for enrolled courses
      const { data: examData } = await supabase
        .from('exam_events').select(`id,exam_date,start_time,end_time,room,semester,cohort,notes,courses(code,name),campuses(name),user_profiles!exam_events_invigilator_id_fkey(full_name)`).in('course_id', courseIds).eq('status', 'published').order('exam_date').order('start_time');

      const mappedExams: ExamEvent[] = (examData || []).map((e: any) => ({
        id: e.id,
        exam_date: e.exam_date,
        start_time: e.start_time,
        end_time: e.end_time,
        room: e.room || '—',
        semester: e.semester,
        cohort: e.cohort,
        notes: e.notes,
        courses: e.courses,
        campuses: e.campuses,
        invigilator: e.user_profiles,
      }));
      setExamEvents(mappedExams);
    } catch (err: any) {
      setError(err.message || 'Failed to load timetable.');
    } finally {
      setLoading(false);
    }
  }, [studentId, semester]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // Set active day to today if available
  useEffect(() => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = dayNames[new Date().getDay()];
    if (DAYS.includes(today)) setActiveDay(today);
  }, []);

  const { slot: nextSlot, targetDate: nextDate } = getNextClassInfo(slots);
  const countdown = useCountdown(nextDate);

  const filteredSlots = activeDay === 'All' ? slots : slots.filter((s) => s.day_of_week === activeDay);

  const handleExportIcal = () => {
    const ical = generateIcal(slots, semester);
    const blob = new Blob([ical], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `silag-timetable-${semester.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };

  if (loading) {
    return (
      <div className="p-5 space-y-4 animate-pulse">
        <div className="h-24 bg-muted rounded-xl" />
        <div className="flex gap-2">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-8 w-16 bg-muted rounded-lg" />)}
        </div>
        {[0, 1, 2].map((i) => <div key={i} className="h-16 bg-muted rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5">
        <div className="flex items-center gap-3 p-4 bg-[var(--negative-bg)] border border-red-200 rounded-xl">
          <ExclamationCircleIcon className="w-5 h-5 text-negative flex-shrink-0" />
          <div>
            <p className="text-sm font-600 text-negative">Failed to load timetable</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
          <button onClick={fetchSlots} className="ml-auto text-xs font-600 text-negative bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4">
      {/* Schedule-change notifications */}
      {scheduleNotifications.length > 0 && (
        <div className="space-y-2">
          {scheduleNotifications.map((n) => (
            <div key={n.id} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <BellIcon className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-600 text-warning">{n.title}</p>
                <p className="text-xs text-amber-700 mt-0.5">{n.body}</p>
              </div>
              <button
                onClick={() => markAsRead(n.id)}
                className="text-xs text-amber-600 hover:text-amber-800 font-600 flex-shrink-0"
                aria-label="Dismiss notification"
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* View toggle: Classes / Exams */}
      <div className="flex items-center gap-1 bg-muted rounded-xl p-1 w-fit">
        <button
          onClick={() => setTimetableView('classes')}
          className={`px-4 py-1.5 rounded-lg text-xs font-600 transition-colors ${
            timetableView === 'classes' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Class Schedule
        </button>
        <button
          onClick={() => setTimetableView('exams')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-600 transition-colors ${
            timetableView === 'exams' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <AcademicCapIcon className="w-3.5 h-3.5" />
          Exams
          {examEvents.length > 0 && (
            <span className="bg-primary text-white text-[10px] font-700 rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
              {examEvents.length}
            </span>
          )}
        </button>
      </div>

      {/* EXAMS VIEW */}
      {timetableView === 'exams' && (
        <div className="space-y-3">
          {examEvents.length === 0 ? (
            <div className="py-12 text-center">
              <AcademicCapIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-600 text-muted-foreground">No exam schedule published yet</p>
              <p className="text-xs text-muted-foreground mt-1">Your exam timetable will appear here once published by the coordinator.</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide">Upcoming Exams</p>
              {examEvents.map((exam) => {
                const examDate = new Date(exam.exam_date + 'T00:00:00');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const daysUntil = Math.ceil((examDate.getTime() - today.getTime()) / 86400000);
                const isPast = daysUntil < 0;
                return (
                  <div
                    key={exam.id}
                    className={`rounded-xl border p-4 transition-colors ${
                      isPast ? 'bg-muted/30 border-border/50 opacity-60' : 'bg-card border-border hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-700 text-primary bg-secondary px-2 py-0.5 rounded">
                            {exam.courses?.code || '—'}
                          </span>
                          {exam.cohort && (
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{exam.cohort}</span>
                          )}
                        </div>
                        <p className="text-sm font-600 text-foreground">{exam.courses?.name || '—'}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarDaysIcon className="w-3.5 h-3.5" />
                            {examDate.toLocaleDateString('en-PG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <ClockIcon className="w-3.5 h-3.5" />
                            {formatTime(exam.start_time)} – {formatTime(exam.end_time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPinIcon className="w-3.5 h-3.5" />
                            {exam.room}{exam.campuses ? `, ${exam.campuses.name}` : ''}
                          </span>
                          {exam.invigilator && (
                            <span className="flex items-center gap-1">
                              <UserIcon className="w-3.5 h-3.5" />
                              {exam.invigilator.full_name}
                            </span>
                          )}
                        </div>
                        {exam.notes && (
                          <p className="text-xs text-muted-foreground mt-2 italic">{exam.notes}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isPast ? (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg">Past</span>
                        ) : daysUntil === 0 ? (
                          <span className="text-xs font-700 text-negative bg-[var(--negative-bg)] px-2 py-1 rounded-lg animate-pulse">Today!</span>
                        ) : daysUntil === 1 ? (
                          <span className="text-xs font-700 text-warning bg-yellow-100 px-2 py-1 rounded-lg">Tomorrow</span>
                        ) : daysUntil <= 7 ? (
                          <span className="text-xs font-700 text-warning bg-yellow-100 px-2 py-1 rounded-lg">In {daysUntil}d</span>
                        ) : (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg">In {daysUntil}d</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* CLASSES VIEW */}
      {timetableView === 'classes' && (
        <>
          {/* Next Class Countdown Card */}
          {nextSlot && (
            <div className="bg-primary rounded-2xl p-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-white/70 text-xs font-600 uppercase tracking-wide mb-1">
                    {countdown?.isNow ? '🟢 Class in Progress' : 'Next Class'}
                  </p>
                  <p className="text-lg font-700">{nextSlot.course_code}</p>
                  <p className="text-white/80 text-sm">{nextSlot.course_name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-white/70">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {nextSlot.day_of_week} · {formatTime(nextSlot.start_time)} – {formatTime(nextSlot.end_time)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPinIcon className="w-3.5 h-3.5" />
                      {nextSlot.room}
                    </span>
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3.5 h-3.5" />
                      {nextSlot.trainer_name}
                    </span>
                  </div>
                </div>
                {countdown && !countdown.isNow && (
                  <div className="flex gap-2 flex-shrink-0">
                    {countdown.days > 0 && (
                      <div className="text-center bg-white/20 rounded-lg px-2.5 py-1.5 min-w-[40px]">
                        <p className="text-lg font-700 font-tabular leading-none">{countdown.days}</p>
                        <p className="text-white/70 text-[10px] mt-0.5">d</p>
                      </div>
                    )}
                    <div className="text-center bg-white/20 rounded-lg px-2.5 py-1.5 min-w-[40px]">
                      <p className="text-lg font-700 font-tabular leading-none">{countdown.hours.toString().padStart(2, '0')}</p>
                      <p className="text-white/70 text-[10px] mt-0.5">h</p>
                    </div>
                    <div className="text-center bg-white/20 rounded-lg px-2.5 py-1.5 min-w-[40px]">
                      <p className="text-lg font-700 font-tabular leading-none">{countdown.minutes.toString().padStart(2, '0')}</p>
                      <p className="text-white/70 text-[10px] mt-0.5">m</p>
                    </div>
                    <div className="text-center bg-white/20 rounded-lg px-2.5 py-1.5 min-w-[40px]">
                      <p className="text-lg font-700 font-tabular leading-none">{countdown.seconds.toString().padStart(2, '0')}</p>
                      <p className="text-white/70 text-[10px] mt-0.5">s</p>
                    </div>
                  </div>
                )}
                {countdown?.isNow && (
                  <span className="flex-shrink-0 bg-white/20 text-white text-xs font-700 px-3 py-1.5 rounded-full animate-pulse">
                    LIVE
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Header row: day filter + iCal export */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setActiveDay('All')}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                  activeDay === 'All' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                All
              </button>
              {DAYS.filter((d) => slots.some((s) => s.day_of_week === d)).map((day) => (
                <button
                  key={day}
                  onClick={() => setActiveDay(day)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                    activeDay === day ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  {DAY_SHORT[day]}
                </button>
              ))}
            </div>
            <button
              onClick={handleExportIcal}
              disabled={slots.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 bg-secondary text-primary hover:bg-secondary/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Export timetable as iCal"
            >
              {exportSuccess ? (
                <>
                  <CheckCircleIcon className="w-4 h-4 text-positive" />
                  <span className="text-positive">Downloaded!</span>
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Export iCal
                </>
              )}
            </button>
          </div>

          {/* Timetable grid */}
          {slots.length === 0 ? (
            <div className="py-12 text-center">
              <CalendarDaysIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-600 text-muted-foreground">No schedule published yet</p>
              <p className="text-xs text-muted-foreground mt-1">Your timetable will appear here once the coordinator publishes it.</p>
            </div>
          ) : filteredSlots.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No classes on {activeDay}.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Group by day when showing All */}
              {activeDay === 'All' ? (
                DAYS.filter((d) => filteredSlots.some((s) => s.day_of_week === d)).map((day) => (
                  <div key={day}>
                    <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2 mt-3 first:mt-0">{day}</p>
                    <div className="space-y-2">
                      {filteredSlots.filter((s) => s.day_of_week === day).map((slot) => (
                        <TimetableSlotCard key={slot.id} slot={slot} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                filteredSlots.map((slot) => <TimetableSlotCard key={slot.id} slot={slot} />)
              )}
            </div>
          )}

          {/* Semester label */}
          {slots.length > 0 && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              Showing schedule for <span className="font-600">{slots[0]?.semester || semester}</span>
            </p>
          )}
        </>
      )}
    </div>
  );
}

function TimetableSlotCard({ slot }: { slot: TimetableSlot }) {
  const isDraft = slot.slot_status === 'draft';
  return (
    <div className={`flex items-stretch gap-3 p-3 rounded-xl border transition-colors ${
      isDraft ? 'bg-muted/40 border-border/50 opacity-70' : 'bg-secondary/40 border-border hover:bg-secondary/60'
    }`}>
      {/* Time column */}
      <div className="flex flex-col items-center justify-center w-14 flex-shrink-0 text-center">
        <p className="text-xs font-700 text-primary leading-none">{formatTime(slot.start_time)}</p>
        <div className="w-px flex-1 bg-border my-1" />
        <p className="text-xs text-muted-foreground leading-none">{formatTime(slot.end_time)}</p>
      </div>
      <div className="w-px bg-border flex-shrink-0" />
      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-xs font-700 text-primary bg-secondary px-2 py-0.5 rounded">{slot.course_code}</span>
          {isDraft && (
            <span className="text-xs font-600 text-muted-foreground bg-muted px-2 py-0.5 rounded">Draft</span>
          )}
        </div>
        <p className="text-sm font-600 text-foreground truncate">{slot.course_name}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <UserIcon className="w-3 h-3 flex-shrink-0" />
            {slot.trainer_name}
          </span>
          <span className="flex items-center gap-1">
            <MapPinIcon className="w-3 h-3 flex-shrink-0" />
            {slot.room}
          </span>
          <span className="flex items-center gap-1">
            <BuildingOfficeIcon className="w-3 h-3 flex-shrink-0" />
            {slot.campus_name}
          </span>
        </div>
      </div>
    </div>
  );
}
